import { MenuOutlined } from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, Modifier } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { TableProps } from 'antd';
import { Table } from 'antd';
import type { HTMLAttributes } from 'react';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import './DraggableTable.scss';

const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

type RowContextProps = {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: ReturnType<typeof useSortable>['listeners'];
};

const RowContext = createContext<RowContextProps>({});

export const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);

  return (
    <MenuOutlined
      ref={setActivatorNodeRef}
      style={{ cursor: 'grab' }}
      {...listeners}
    />
  );
};

type RowProps = HTMLAttributes<HTMLTableRowElement> & {
  'data-row-key': number;
};

const DraggableBodyRow = ({ ...props }: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

type Props<RecordType> = {
  dataSource: RecordType[];
  onSortEnd: ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => void | Promise<void>;
} & Omit<TableProps<RecordType>, 'dataSource'>;

export const DraggableTable = <RecordType extends { id: number }>({
  ...props
}: Props<RecordType>) => {
  const { dataSource, onSortEnd } = props;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const [orderedData, setOrderedData] = useState(dataSource);

  const dataSourceKey = useMemo(
    () => dataSource.map((item) => item.id).join(','),
    [dataSource]
  );

  useEffect(() => {
    setOrderedData(dataSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resync keyed on id order, not array identity
  }, [dataSourceKey]);

  const items = useMemo(
    () => orderedData.map((item) => item.id),
    [orderedData]
  );

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = orderedData.findIndex((item) => item.id === active.id);
    const newIndex = orderedData.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    setOrderedData((prev) => arrayMove(prev, oldIndex, newIndex));

    try {
      await onSortEnd({ oldIndex, newIndex });
    } catch {
      setOrderedData(orderedData);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <Table
          rowKey="id"
          className={props.className}
          {...props}
          dataSource={orderedData}
          components={{
            ...props.components,
            body: {
              row: DraggableBodyRow,
            },
          }}
        />
      </SortableContext>
    </DndContext>
  );
};
